import { useCallback, useEffect, useState } from 'react';
import { useIsLibraryUploadRiskOptinEnabled } from '../../flags';

interface LibraryRiskOptinGateConfig {
  /** Whether the user has already accepted the risk opt-in (DB-backed). */
  hasAccepted: boolean;
  /** Persists acceptance to the user record. */
  accept: () => void;
  /** True while the user record (acceptance state) is still loading. */
  isPending?: boolean;
}

export interface LibraryRiskOptinGate {
  /** True while the opt-in must be shown before the gated UI is revealed. */
  needsOptin: boolean;
  /** Mandatory acceptance checkbox state (resets when the gate re-arms). */
  checkboxAccepted: boolean;
  setCheckboxAccepted: (accepted: boolean) => void;
  /** Persists acceptance and reveals the gated UI. */
  confirm: () => void;
}

/**
 * Shared gate for the Evidence library risk opt-ins (EVI-1566). Centralises the
 * "is the flag on AND has the user not yet accepted?" decision plus the
 * mandatory-checkbox state, so each entry point (Upload modal, New Collection
 * Documents/Sources tabs) stays a thin render of {@link LibraryRiskOptin}.
 *
 * Both opt-ins are gated behind the single `useIsLibraryUploadRiskOptinEnabled`
 * flag — they are the same opt-in experience, differing only in copy and the
 * durable acceptance key.
 */
export const useLibraryRiskOptinGate = ({
  hasAccepted,
  accept,
  isPending = false,
}: LibraryRiskOptinGateConfig): LibraryRiskOptinGate => {
  const isEnabled = useIsLibraryUploadRiskOptinEnabled();
  const [needsOptin, setNeedsOptin] = useState(false);
  const [checkboxAccepted, setCheckboxAccepted] = useState(false);

  useEffect(() => {
    setNeedsOptin(isEnabled && !isPending && !hasAccepted);
    setCheckboxAccepted(false);
  }, [isEnabled, isPending, hasAccepted]);

  const confirm = useCallback(() => {
    accept();
    setNeedsOptin(false);
  }, [accept]);

  return { needsOptin, checkboxAccepted, setCheckboxAccepted, confirm };
};
